/**
 * AppSidebar.tsx - Sidebar de navegação do shell do Órbita.
 *
 * Referência: NAVEGACAO.md §3.3, UX.md §2.3 (layout shell).
 * Adapta o padrão sidebar.tsx do ALADDIN para os módulos e grupos do Órbita.
 *
 * Comportamento:
 *   - md+ (≥768px): 240px expandida por padrão (ícone + label sempre visível);
 *     o usuário pode recolher para 64px só ícones (collapsed=true) pelo botão da
 *     sidebar, com a escolha persistida. No modo recolhido o hover revela os
 *     rótulos temporariamente (collapsed volta a false).
 *   - Mobile (<768px): oculta; drawer aberto via AppShell
 */

import { NavLink } from "react-router";
import { useVisibleNav } from "./use-visible-nav";

// ---------------------------------------------------------------------------
// Ícones minimalistas (SVG inline, sem dependência externa no shell base)
// ---------------------------------------------------------------------------

// Paths SVG por moduleId, tipados explicitamente para evitar `unsafe assignment`
const NAV_ICON_PATHS: Record<string, string> = {
  "visao-executiva":       "M3 12L12 3l9 9M5 10v9h5v-6h4v6h5v-9",
  "fluxo-caixa":           "M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  "fluxo-matriz":          "M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18",
  "contas-a-receber":      "M12 3v12m0 0l-4-4m4 4l4-4M5 21h14",
  "contas-a-pagar":        "M12 21V9m0 0l-4 4m4-4l4 4M5 3h14",
  "recebimentos":          "M9 14l6-6m-3-2a7 7 0 100 14A7 7 0 006 6z",
  "importacao-pagamentos": "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3",
  "compras":               "M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0",
  "estoque":               "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  "custos":                "M4 7h16M4 11h16M4 15h16M4 19h16",
  "rateios":               "M10 3H3v7h7V3zM21 3h-7v7h7V3zM21 14h-7v7h7v-7zM10 14H3v7h7v-7z",
  "precificacao":          "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  "vendas":                "M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01",
  "procedimentos":         "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  "assistencial":          "M22 12h-4l-3 9L9 3l-3 9H2",
  "estrutura":             "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  "integracoes":           "M8 9l3 3-3 3M13 15h3M6 20a6 6 0 000-12h1M18 20a6 6 0 000-12h-1",
  "usuarios":              "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
  "configuracoes":         "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z",
};

function NavIcon({ moduleId }: { moduleId: string }) {
  const d = NAV_ICON_PATHS[moduleId];
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {d ? <path d={d} /> : <circle cx="12" cy="12" r="4" />}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

interface AppSidebarProps {
  /** Modo drawer (mobile): oculta header próprio da sidebar, sidebar gerencia seu próprio scroll */
  isDrawer?: boolean;
  /**
   * Modo recolhido (64px, só ícones): mostra só os ícones centralizados, sem
   * labels de item nem labels de grupo. Ativado por escolha do usuário (botão
   * de recolher, persistida). No hover/foco desse modo o AppShell passa
   * collapsed=false, revelando ícone + label temporariamente.
   */
  collapsed?: boolean;
  onNavClick?: () => void;
}

export function AppSidebar({ isDrawer = false, collapsed = false, onNavClick }: AppSidebarProps) {
  // Modelo do nav visível (RBAC ∩ config-admin) vem pronto do hook; a sidebar só
  // APRESENTA. A filtragem que vivia inline aqui foi extraída (CONFIGURACOES-MENU
  // -DESENHO §4.1 / §8 Parte 7).
  const visibleGroups = useVisibleNav();

  return (
    <nav
      aria-label="Navegação principal"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        padding: isDrawer ? "1rem 0" : "0",
      }}
    >
      {/* Logo do tenant + wordmark (apenas quando não é drawer).
          No rail colapsado, o anel é centralizado e o wordmark é omitido. */}
      {!isDrawer && (
        <div
          style={{
            padding: collapsed ? "1.25rem 0 1rem" : "1.25rem 1rem 1rem",
            borderBottom: "1px solid var(--color-nav-border)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: "0.5rem",
            }}
          >
            {/* Logo oficial do Órbita (a MESMA do login e da institucional:
                /orbita/assets/logo-orbita.png, copiada para public/ e servida
                via BASE_URL para funcionar em dev e no hosting /orbita/app/).
                Rail colapsado: só o símbolo (anel + pontos). Expandido: wordmark. */}
            <img
              src={`${import.meta.env.BASE_URL}${collapsed ? "logo-orbita-simbolo.svg" : "logo-orbita.png"}`}
              alt="Órbita"
              style={{ height: "26px", width: "auto", objectFit: "contain", display: "block" }}
            />
          </div>
        </div>
      )}

      {/* Grupos de navegação */}
      <div style={{ flex: 1, padding: "0.5rem 0" }}>
        {visibleGroups.map((group) => {
          // Grupos e itens já vêm filtrados (RBAC ∩ config) pelo useVisibleNav,
          // inclusive sem grupos vazios; aqui só apresentamos.
          return (
            <div key={group.id} style={{ marginBottom: "0.25rem" }}>
              {/* Label do grupo: o sidebar tem fundo navy (--color-nav-bg),
                  então a cor precisa ser clara para legibilidade (WCAG AA).
                  O token --color-nav-group-label garante ~6:1 sobre navy; os
                  tokens de texto do tema claro ficavam ilegiveis aqui.
                  No rail colapsado (só ícones) o label de grupo é omitido para
                  não gerar texto cortado. */}
              {!collapsed && (
                <p
                  style={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    color: "var(--color-nav-group-label)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    padding: "0.75rem 1rem 0.25rem",
                    margin: 0,
                  }}
                >
                  {group.label}
                </p>
              )}

              {/* Itens */}
              {group.items.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={onNavClick}
                  title={item.label}
                  aria-label={item.label}
                  /* Sidebar de fundo navy (--color-nav-bg): tokens nav claros
                     (ciano accent + texto claro), nao os tokens do tema claro.
                     Inativo --color-nav-item-inactive (~10:1 sobre navy, WCAG
                     AA); ativo --color-nav-active com faixa/borda ciano; hover
                     clareia (--color-nav-hover / --color-nav-hover-text).
                     No rail colapsado o ícone é centralizado (sem label). */
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: collapsed ? "0" : "0.625rem",
                    minHeight: "44px",
                    padding: collapsed ? "0" : "0 1rem",
                    textDecoration: "none",
                    fontSize: "0.8125rem",
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: isActive ? 600 : 500,
                    borderLeft: isActive ? "2px solid var(--color-nav-active)" : "2px solid transparent",
                    background: isActive ? "var(--color-nav-active-subtle)" : "transparent",
                    color: isActive ? "var(--color-nav-active)" : "var(--color-nav-item-inactive)",
                    transition: "background 150ms, color 150ms",
                  })}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    if (!el.getAttribute("aria-current")) {
                      el.style.background = "var(--color-nav-hover)";
                      el.style.color = "var(--color-nav-hover-text)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    if (!el.getAttribute("aria-current")) {
                      // Restaura os valores EXPLÍCITOS do estado inativo. Resetar
                      // para "" removia a cor inline que o React aplicou e, sem
                      // re-render, o token nunca voltava: o texto herdava uma cor
                      // escura sobre o navy e "sumia" após o hover.
                      el.style.background = "transparent";
                      el.style.color = "var(--color-nav-item-inactive)";
                    }
                  }}
                >
                  <NavIcon moduleId={item.id} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
